/**
 * Biometric Authentication Service
 * 
 * Servicio para autenticación biométrica (Face ID / Touch ID).
 * REQUERIDO para aprobación de Apple App Store (Directriz 4.2)
 * Especialmente importante para apps de salud mental con datos sensibles.
 * 
 * SEGURIDAD WEB:
 * En plataformas web, usa webAuthGuardService para solicitar
 * re-autenticación con contraseña/PIN antes de mostrar datos sensibles.
 * 
 * @module native/biometric-auth
 */

import { NativeBiometric, BiometryType, Credentials } from 'capacitor-native-biometric';
import { Capacitor } from '@capacitor/core';
import { Logger } from '@/core/utils';
import { webAuthGuardService } from '@/core/security/web-auth-guard.service';

const logger = Logger.getLogger('BiometricAuthService');

export interface BiometricStatus {
  isAvailable: boolean;
  biometryType: 'face' | 'fingerprint' | 'iris' | 'none';
  hasCredentials: boolean;
}

export interface BiometricOptions {
  reason: string;
  title?: string;
  subtitle?: string;
  description?: string;
  negativeButtonText?: string;
  maxAttempts?: number;
  useFallback?: boolean;
}

class BiometricAuthService {
  private static instance: BiometricAuthService;
  private readonly SERVER_ID = 'com.appmor.app.biometric';
  private biometryType: BiometryType = BiometryType.NONE;
  private isAvailable = false;

  private constructor() {}

  static getInstance(): BiometricAuthService {
    if (!BiometricAuthService.instance) {
      BiometricAuthService.instance = new BiometricAuthService();
    }
    return BiometricAuthService.instance;
  }

  /**
   * Verifica si estamos en un dispositivo nativo
   */
  isNativePlatform(): boolean {
    return Capacitor.isNativePlatform();
  }

  /**
   * Inicializa y verifica disponibilidad de biometría
   */
  async initialize(): Promise<BiometricStatus> {
    if (!this.isNativePlatform()) {
      logger.info('Biometría no disponible en web');
      return {
        isAvailable: false,
        biometryType: 'none',
        hasCredentials: false,
      };
    }

    try {
      const result = await NativeBiometric.isAvailable();
      this.isAvailable = result.isAvailable;
      this.biometryType = result.biometryType;

      const biometryName = this.getBiometryTypeName(result.biometryType);
      logger.info('Estado de biometría', { 
        available: result.isAvailable,
        type: biometryName 
      });

      // Verificar si hay credenciales guardadas
      const hasCredentials = await this.hasStoredCredentials();

      return {
        isAvailable: result.isAvailable,
        biometryType: biometryName,
        hasCredentials,
      };
    } catch (error) {
      logger.error('Error verificando disponibilidad de biometría', { error });
      return {
        isAvailable: false,
        biometryType: 'none',
        hasCredentials: false,
      };
    }
  }

  /**
   * Verifica la biometría del usuario (para proteger acceso a datos sensibles)
   * 
   * SEGURIDAD MEJORADA:
   * - En apps nativas: usa Face ID / Touch ID
   * - En web: solicita contraseña o PIN de sesión
   * 
   * NOTA: Verificación de contraseña en web deshabilitada temporalmente
   * para mejor UX. La sesión de Firebase ya proporciona autenticación.
   */
  async verifyIdentity(options?: Partial<BiometricOptions>): Promise<boolean> {
    // DESHABILITADO: Verificación de contraseña en web (muy molesta para UX)
    // La autenticación de Firebase ya protege el acceso a datos
    if (!this.isNativePlatform()) {
      logger.info('Plataforma web: verificación biométrica omitida (sesión Firebase activa)');
      return true; // Confiar en la sesión de Firebase
    }
    
    // En nativo pero sin biometría disponible: también omitir
    if (!this.isAvailable) {
      logger.info('Biometría no disponible: verificación omitida');
      return true;
    }

    const defaultOptions: BiometricOptions = {
      reason: 'Verifica tu identidad para acceder a información sensible',
      title: 'Autenticación requerida',
      subtitle: 'Appmor',
      description: 'Usa Face ID o Touch ID para continuar',
      negativeButtonText: 'Cancelar',
      maxAttempts: 3,
      useFallback: true,
    };

    const finalOptions = { ...defaultOptions, ...options };

    try {
      await NativeBiometric.verifyIdentity({
        reason: finalOptions.reason,
        title: finalOptions.title,
        subtitle: finalOptions.subtitle,
        description: finalOptions.description,
        negativeButtonText: finalOptions.negativeButtonText,
        maxAttempts: finalOptions.maxAttempts,
        useFallback: finalOptions.useFallback,
      });

      logger.info('Verificación biométrica exitosa');
      return true;
    } catch (error) {
      logger.warn('Verificación biométrica fallida', { error });
      return false;
    }
  }

  /**
   * Guarda credenciales de forma segura usando biometría
   */
  async saveCredentials(username: string, password: string): Promise<boolean> {
    if (!this.isNativePlatform() || !this.isAvailable) {
      logger.warn('No se pueden guardar credenciales, biometría no disponible');
      return false;
    }

    try {
      await NativeBiometric.setCredentials({
        username,
        password,
        server: this.SERVER_ID,
      });
      
      logger.info('Credenciales guardadas de forma segura');
      return true;
    } catch (error) {
      logger.error('Error guardando credenciales', { error });
      return false;
    }
  }

  /**
   * Obtiene credenciales guardadas (requiere verificación biométrica)
   */
  async getCredentials(): Promise<Credentials | null> {
    if (!this.isNativePlatform() || !this.isAvailable) {
      return null;
    }

    try {
      // Primero verificar identidad
      const verified = await this.verifyIdentity({
        reason: 'Verifica tu identidad para obtener tus credenciales',
      });

      if (!verified) {
        return null;
      }

      const credentials = await NativeBiometric.getCredentials({
        server: this.SERVER_ID,
      });

      logger.info('Credenciales obtenidas correctamente');
      return credentials;
    } catch (error) {
      logger.error('Error obteniendo credenciales', { error });
      return null;
    }
  }

  /**
   * Verifica si hay credenciales guardadas
   */
  async hasStoredCredentials(): Promise<boolean> {
    if (!this.isNativePlatform()) {
      return false;
    }

    try {
      await NativeBiometric.getCredentials({
        server: this.SERVER_ID,
      });
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Elimina credenciales guardadas
   */
  async deleteCredentials(): Promise<boolean> {
    if (!this.isNativePlatform()) {
      return false;
    }

    try {
      await NativeBiometric.deleteCredentials({
        server: this.SERVER_ID,
      });
      logger.info('Credenciales eliminadas');
      return true;
    } catch (error) {
      logger.error('Error eliminando credenciales', { error });
      return false;
    }
  }

  /**
   * Obtiene el tipo de biometría disponible
   */
  getBiometryType(): 'face' | 'fingerprint' | 'iris' | 'none' {
    return this.getBiometryTypeName(this.biometryType);
  }

  /**
   * Convierte el tipo de biometría a string legible
   */
  private getBiometryTypeName(type: BiometryType): 'face' | 'fingerprint' | 'iris' | 'none' {
    switch (type) {
      case BiometryType.FACE_ID:
      case BiometryType.FACE_AUTHENTICATION:
        return 'face';
      case BiometryType.TOUCH_ID:
      case BiometryType.FINGERPRINT:
        return 'fingerprint';
      case BiometryType.IRIS_AUTHENTICATION:
        return 'iris';
      default:
        return 'none';
    }
  }

  /**
   * Obtiene el nombre amigable del tipo de biometría
   */
  getBiometryDisplayName(): string {
    const type = this.getBiometryType();
    switch (type) {
      case 'face':
        return 'Face ID';
      case 'fingerprint':
        return 'Touch ID';
      case 'iris':
        return 'Iris';
      default:
        return 'No disponible';
    }
  }

  /**
   * Protege el acceso a una función con biometría
   * Útil para datos sensibles de salud mental
   */
  async protectWithBiometric<T>(
    action: () => Promise<T>,
    reason?: string
  ): Promise<T | null> {
    const verified = await this.verifyIdentity({
      reason: reason || 'Verifica tu identidad para acceder a esta información',
    });

    if (!verified) {
      logger.warn('Acceso denegado por fallo en verificación biométrica');
      return null;
    }

    return action();
  }
}

export const biometricAuthService = BiometricAuthService.getInstance();






